import { useCallback, useEffect, useRef, useState } from 'react';
import { Info, RefreshCw, Settings2 } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipTrigger, cn } from 'shared-ui';

import type { NetworkStats } from '@/api/statsApi';
import { STAT_DEFINITIONS, type StatId } from '@/hooks/useStatsPreferences';
import { useMapActions } from '@/map-actions/useMapActions';
import { useExclusivePanel } from '@/panels/useExclusivePanel';
import { percentOfTotal } from '@/utils/percentOfTotal';

import { StatItem, StatItemSkeleton } from './StatItem';

interface StatsBarProps {
    data: NetworkStats | null;
    /**
     * Network-wide totals used to derive each stat's "% of total" sub-line and the
     * helper strip. Only supplied when `data` is scoped to an Area of Interest or
     * DMA; null renders the classic network view with no percentages.
     */
    networkTotals?: NetworkStats | null;
    /** Display name of the scoped Area of Interest / DMA. Null in the network view. */
    scopeLabel?: string | null;
    isLoading: boolean;
    isError: boolean;
    visibleStats: readonly StatId[];
    /** Opens the per-user stats configuration behind the Pin Stats control. */
    onOpenConfig: () => void;
}

const SKELETON_COUNT = 6;

export function StatsBar({
    data,
    networkTotals = null,
    scopeLabel = null,
    isLoading,
    isError,
    visibleStats,
    onOpenConfig,
}: StatsBarProps) {
    const visibleDefs = STAT_DEFINITIONS.filter((def) => visibleStats.includes(def.id));

    // Any open sliding panel (Layers/Filters/Download/POI/Stats) draws its own left
    // border flush against this bar, so the Pin Stats button only draws its right
    // border while no panel is open — otherwise the divider doubles up.
    const { activePanel } = useExclusivePanel();

    // Reset (in the scoped helper strip) returns the map and these stats to the network view:
    // flies out, drops any clicked AOI/DMA and restores the network totals — the same behaviour
    // as the locate-network map control, dispatched through the map-actions bridge because the
    // map lives in MapView, not this tree.
    const { flyToNetwork } = useMapActions();

    const scrollRef = useRef<HTMLDivElement>(null);
    const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

    // Scroll affordances, only shown when the stats actually overflow: `canScroll` drives the grab
    // cursor (you can always grab to scroll while overflowing), `canScrollRight` drives the
    // right-edge gradient (only while more content remains to the right).
    const [scroll, setScroll] = useState({ canScroll: false, canScrollRight: false });

    const updateScrollAffordance = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const overflow = el.scrollWidth - el.clientWidth;
        // Measured from the live DOM after render; setting state here is intentional.
        // eslint-disable-next-line react-x/set-state-in-effect
        setScroll({ canScroll: overflow > 1, canScrollRight: overflow - el.scrollLeft > 1 });
    }, []);

    // Recompute when the rendered stats change (count/content affects scrollWidth).
    useEffect(() => {
        updateScrollAffordance();
    }, [updateScrollAffordance, visibleStats, data, networkTotals, scopeLabel, isLoading, isError]);

    // Recompute when the bar is resized (clientWidth changes).
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(updateScrollAffordance);
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [updateScrollAffordance]);

    function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
        const el = scrollRef.current;
        if (!el || !scroll.canScroll) return;
        drag.current = { active: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
        el.style.cursor = 'grabbing';
    }

    function stopDrag() {
        drag.current.active = false;
        const el = scrollRef.current;
        // Clear the inline override so the className-driven cursor (grab when scrollable) applies.
        if (el) el.style.cursor = '';
    }

    function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
        if (!drag.current.active) return;
        const el = scrollRef.current;
        if (!el) return;
        e.preventDefault();
        el.scrollLeft = drag.current.scrollLeft - (e.pageX - el.offsetLeft - drag.current.startX);
    }

    function renderContent() {
        if (isLoading && !data) {
            // Full skeleton only on first load; if stale data is present, show it while fetching.
            return Array.from({ length: SKELETON_COUNT }, (_, i) => <StatItemSkeleton key={i} />);
        }
        if (isError && !data) {
            return (
                <div className="flex flex-1 items-center px-4">
                    <span className="text-destructive text-xs">Unable to load stats — please try again.</span>
                </div>
            );
        }
        return visibleDefs.map((def) => (
            <StatItem
                key={def.id}
                label={def.label}
                value={data?.[def.id] ?? null}
                format={def.format}
                percentOfTotal={networkTotals ? percentOfTotal(data?.[def.id], networkTotals[def.id]) : null}
            />
        ));
    }

    // Helper strip shown below the bar when scoped to an Area of Interest / DMA,
    // anchored on the leaks-found contribution. Hidden when the network total is
    // zero/unavailable (no meaningful percentage to state).
    const leaksPercent = scopeLabel !== null ? percentOfTotal(data?.leaksFound, networkTotals?.leaksFound) : null;

    return (
        <>
            <div className="border-border bg-background flex h-14 w-full shrink-0 items-stretch border-b max-md:border-t">
                {/* Scrollable stat items */}
                <div
                    ref={scrollRef}
                    className={cn(
                        'divide-border flex min-w-0 items-stretch divide-x overflow-x-auto select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                        scroll.canScroll && 'cursor-grab',
                    )}
                    onMouseDown={handleMouseDown}
                    onMouseUp={stopDrag}
                    onMouseLeave={stopDrag}
                    onMouseMove={handleMouseMove}
                    onScroll={updateScrollAffordance}
                >
                    {renderContent()}
                </div>

                {/* Pin Stats button */}
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <Button
                                variant="ghost"
                                onClick={onOpenConfig}
                                aria-label="Pin Stats"
                                className={cn(
                                    'border-border hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent h-14 w-14 shrink-0 cursor-pointer rounded-none border-y-0! border-l bg-transparent',
                                    activePanel === null ? 'border-r' : 'border-r-0!',
                                    scroll.canScrollRight &&
                                        '[box-shadow:-30px_0_20px_0px_rgba(0,0,0,0.08)] dark:[box-shadow:-30px_0_20px_0px_rgba(0,0,0,0.5)]',
                                )}
                            >
                                <Settings2 className="text-origin-blue size-4" strokeWidth={2.25} />
                            </Button>
                        }
                    />
                    <TooltipContent side="top">Pin Stats</TooltipContent>
                </Tooltip>
            </div>

            {scopeLabel !== null && leaksPercent !== null && (
                <div className="border-border bg-background text-muted-foreground flex h-11 w-full shrink-0 items-center gap-1.5 border-b px-4 text-xs">
                    <Info className="text-muted-foreground size-3.5 shrink-0" strokeWidth={2.25} />
                    <span className="truncate">
                        <span className="text-foreground font-medium">{scopeLabel}</span> represents{' '}
                        {/* Percentage matches the scope name: foreground colour, Poppins Medium. */}
                        <span className="text-foreground leading-4 font-medium tracking-normal">
                            {leaksPercent}%
                        </span>{' '}
                        of total leaks in network
                    </span>
                    <button
                        type="button"
                        onClick={flyToNetwork}
                        className="text-origin-blue ml-auto flex shrink-0 cursor-pointer items-center gap-1 text-sm leading-5 font-semibold tracking-normal"
                    >
                        Reset
                        <RefreshCw className="size-4" strokeWidth={2} />
                    </button>
                </div>
            )}
        </>
    );
}
